import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useEcho } from '@laravel/echo-react';
import {
  applyCommentResolutionUpdateToCache,
  applyDeletedCommentTombstoneToCache,
  commentMatchesVisibilityFilter,
  insertCommentIntoCache,
  isOptimisticReplyId,
  mergeRepliesById,
  normalizeCommentBody,
  removeNodeCommentFromCache,
  resolveInsertPositionForPagedQuery,
  shouldInsertNewComment,
} from '@/lib/comments';
import {
  type Comment as CommentData,
  CommentResolutionFilter,
  type CommentResolutionFilterOption,
  type CommentVisibilityFilterOption,
  type GetNodeCommentsResponse,
  type NodeComment,
} from '@/types/comments';
import { applyUpdatedCommentToNodeCommentsResponse } from '@/hooks/comments/useUpdateNodeCommentMutation';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { useShareConfig } from '@/context/ShareConfigContext';
import { useAccount } from '@/hooks/data/account/useAccount';
import { isDefined } from '@/utilities/guards';
import { QueryKey } from '@/lib/queryKeys';

export interface CommentCreatedEvent {
  commentId: string;
  nodeId: string;
  parentId: string | null;
  // Only meaningful for root comments (parentId === null) — replies are never private.
  private?: boolean | null;
  comment: {
    id: string;
    body: string;
    createdBy: {
      id: string;
      name: string;
    } | null;
    createdAt: string;
    updatedAt: string;
    parentId: string | null;
  };
}

export interface CommentResolutionChangedEvent {
  commentId: string;
  nodeId: string;
  resolvedAt: string | null;
  resolvedBy: {
    id: string;
    name: string;
  } | null;
}

export type CommentDeletedEvent = {
  commentId: string;
  nodeId: string;
  parentId: string | null;
};

const OPTIMISTIC_REPLY_MATCH_WINDOW_MS = 30_000;

const getCommentTimestamp = (value: string) => {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const isMatchingOptimisticReply = ({
  optimisticReply,
  nextReply,
}: {
  optimisticReply: CommentData;
  nextReply: CommentData;
}) => {
  if (!isOptimisticReplyId(optimisticReply.id)) {
    return false;
  }

  if (optimisticReply.parentId !== nextReply.parentId) {
    return false;
  }

  const optimisticReplyAuthorId = optimisticReply.createdBy?.id ?? null;
  const nextReplyAuthorId = nextReply.createdBy?.id ?? null;

  if (optimisticReplyAuthorId !== nextReplyAuthorId) {
    return false;
  }

  if (normalizeCommentBody(optimisticReply.body ?? '') !== normalizeCommentBody(nextReply.body ?? '')) {
    return false;
  }

  const optimisticReplyTimestamp = getCommentTimestamp(optimisticReply.createdAt);
  const nextReplyTimestamp = getCommentTimestamp(nextReply.createdAt);

  if (optimisticReplyTimestamp === null || nextReplyTimestamp === null) {
    return true;
  }

  return Math.abs(nextReplyTimestamp - optimisticReplyTimestamp) <= OPTIMISTIC_REPLY_MATCH_WINDOW_MS;
};

export const useNodeCommentsChannel = ({
  nodeId,
  hasNextPage,
  sortOrder = SortOrderOptions.DESC,
  sortValue = SortOrderValues.CREATED_AT,
  resolutionFilter,
  visibilityFilter,
  onNewComment,
  scrollContainerRef,
}: {
  nodeId: string;
  hasNextPage: boolean;
  sortOrder?: string;
  sortValue?: string;
  resolutionFilter?: CommentResolutionFilterOption;
  visibilityFilter?: CommentVisibilityFilterOption;
  onNewComment?: (commentId: string, insertPosition: 'top' | 'bottom') => void;
  scrollContainerRef?: MutableRefObject<HTMLDivElement | null>;
}) => {
  const queryClient = useQueryClient();
  const hasNextPageRef = useRef(hasNextPage);
  const shareConfig = useShareConfig();
  const shareId = shareConfig?.id;
  const { profile } = useAccount();
  const userId = profile?.user?.id;

  // Keep ref updated for use in callbacks
  useEffect(() => {
    hasNextPageRef.current = hasNextPage;
  }, [hasNextPage]);

  // Pair-keyed to the current user: a share can cover many nodes (e.g. an album
  // share), so events for other nodes are filtered out by nodeId in each handler
  // below rather than by subscribing to a per-node channel.
  // Fall back to the node channel until the profile query resolves userId - subscribing
  // to `share.{shareId}.user.${undefined}` would land on `private-undefined` and 403.
  const channelName = useMemo(() => {
    if (!shareId || !userId) {
      return `node.${nodeId}.comments`;
    }

    return `share.${shareId}.user.${userId}`;
  }, [nodeId, shareId, userId]);

  // Handle comment created events
  const handleCommentCreated = useCallback(
    (event: CommentCreatedEvent) => {
      if (event.nodeId !== nodeId || event.parentId) {
        return;
      }

      if (resolutionFilter === CommentResolutionFilter.RESOLVED) {
        return;
      }

      if (!commentMatchesVisibilityFilter(event.private, visibilityFilter)) {
        return;
      }

      const currentInsertPosition = shouldInsertNewComment({
        sortOrder,
        sortValue,
        hasNextPage: hasNextPageRef.current,
      });

      if (currentInsertPosition === 'none') {
        // Don't insert - just invalidate so next fetch gets new data
        void queryClient.invalidateQueries({
          queryKey: [QueryKey.NodeCommentList, nodeId, shareId],
        });
        return;
      }

      // Create NodeComment structure from the event
      const newNodeComment: NodeComment = {
        commentId: event.commentId,
        nodeId: event.nodeId,
        comment: {
          id: event.comment.id,
          body: event.comment.body,
          createdBy: event.comment.createdBy,
          createdAt: event.comment.createdAt,
          updatedAt: event.comment.updatedAt,
          parentId: event.comment.parentId,
        },
        latestReplies: [],
        repliesCount: 0,
        resolvedAt: null,
        resolvedBy: null,
        private: event.private ?? false,
      };

      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.findAll({
        queryKey: [QueryKey.NodeCommentList, nodeId, shareId],
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const [, , , querySortOrder, querySortValue, queryResolutionFilter, queryVisibilityFilter] =
            query.queryKey as [
              QueryKey,
              string,
              string | undefined,
              string,
              string,
              CommentResolutionFilterOption?,
              CommentVisibilityFilterOption?,
            ];

          if (queryResolutionFilter === CommentResolutionFilter.RESOLVED) {
            return oldData;
          }

          if (!commentMatchesVisibilityFilter(event.private, queryVisibilityFilter)) {
            return oldData;
          }

          const insertPosition = resolveInsertPositionForPagedQuery({
            oldData,
            sortOrder: querySortOrder ?? sortOrder,
            sortValue: querySortValue ?? sortValue,
          });

          return insertCommentIntoCache({
            oldData,
            newNodeComment,
            insertPosition,
          });
        });
      });

      // Notify parent component about new comment for scroll handling
      onNewComment?.(event.commentId, currentInsertPosition);
    },
    [nodeId, queryClient, resolutionFilter, visibilityFilter, sortOrder, sortValue, onNewComment, shareId],
  );

  const handleCommentResolutionChanged = useCallback(
    (event: CommentResolutionChangedEvent) => {
      if (event.nodeId !== nodeId) {
        return;
      }

      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.findAll({
        queryKey: [QueryKey.NodeCommentList, nodeId, shareId],
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return applyCommentResolutionUpdateToCache({
            oldData,
            commentId: event.commentId,
            resolvedAt: event.resolvedAt,
            resolvedBy: event.resolvedBy,
          });
        });
      });
    },
    [nodeId, queryClient, sortOrder, sortValue, shareId],
  );

  const handleCommentUpdated = useCallback(
    (event: CommentCreatedEvent) => {
      if (event.nodeId !== nodeId) {
        return;
      }

      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.findAll({ queryKey: [QueryKey.NodeCommentList, nodeId, shareId], exact: false });

      // Only root comments carry the private flag — a reply update never affects visibility filtering.
      // Treat a missing parentId as a root update: over-applying the privacy branches is harmless,
      // skipping them would leave a private comment visible in a filtered view.
      const isRootCommentUpdate = !isDefined(event.parentId);

      // Queries we can't safely patch in place (see below) — refetched once the loop below finishes,
      // rather than from inside the setQueryData updaters themselves.
      const queryKeysNeedingRefetch: Array<ReadonlyArray<unknown>> = [];

      queries.forEach((query) => {
        queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          // On a share, a comment that just became private must disappear from the
          // collaborators' view entirely — they never have visibility into private comments there.
          // Root comments only: a reply update never changes thread visibility (see line 319).
          if (shareId && isRootCommentUpdate && event.private) {
            return removeNodeCommentFromCache({ oldData, commentId: event.commentId });
          }
          const [, , , , , , queryVisibilityFilter] = query.queryKey as [
            QueryKey,
            string,
            string | undefined,
            string,
            string,
            CommentResolutionFilterOption?,
            CommentVisibilityFilterOption?,
          ];

          const isRootAlreadyCached = oldData.pages.some((page) =>
            page.comments.some((comment) => comment.commentId === event.commentId),
          );
          const isReplyAlreadyCached =
            !isRootAlreadyCached &&
            oldData.pages.some((page) =>
              page.comments.some((comment) => comment.latestReplies.some((reply) => reply.id === event.commentId)),
            );
          const isAlreadyCached = isRootAlreadyCached || isReplyAlreadyCached;

          if (isAlreadyCached) {
            // The comment's own privacy just changed such that it no longer belongs under this
            // query's active visibility filter (e.g. filter=Public, comment just went private) —
            // drop it rather than patch its fields in place.
            if (
              isRootAlreadyCached &&
              isRootCommentUpdate &&
              event.private !== undefined &&
              !commentMatchesVisibilityFilter(event.private, queryVisibilityFilter)
            ) {
              return removeNodeCommentFromCache({ oldData, commentId: event.commentId });
            }

            return applyUpdatedCommentToNodeCommentsResponse(oldData, {
              commentId: event.commentId,
              updatedComment: event.comment,
              private: event.private,
            });
          }

          // Not cached yet, but the comment's privacy just changed so that it now matches this
          // query's active visibility filter (e.g. filter=Private, a public comment just went
          // private) — it may have been invisible here the whole time. This event carries only
          // the root comment's own fields — no replies, repliesCount, resolvedAt/By, or reliable
          // sort position — so reconstructing a NodeComment here would always show it with 0
          // replies (and could land in the wrong spot). Refetch instead of guessing, so it
          // reappears exactly as a fresh load would show it: root comment, "X more replies"
          // separator, last N replies.
          if (
            isRootCommentUpdate &&
            event.private !== undefined &&
            queryVisibilityFilter &&
            commentMatchesVisibilityFilter(event.private, queryVisibilityFilter)
          ) {
            queryKeysNeedingRefetch.push(query.queryKey);
            return oldData;
          }

          // On a share, a comment turning public again (private: false) may have been invisible
          // here the whole time regardless of the visibility filter above (shares exclude private
          // comments outright) — same refetch rationale as above.
          if (shareId && !event.private) {
            queryKeysNeedingRefetch.push(query.queryKey);
          }

          return oldData;
        });
      });

      queryKeysNeedingRefetch.forEach((queryKey) => {
        void queryClient.invalidateQueries({ queryKey, exact: true });
      });
    },
    [nodeId, queryClient, shareId],
  );

  // Handle live update reply created event
  const handleReplyCreated = useCallback(
    (event: CommentCreatedEvent) => {
      if (event.nodeId !== nodeId || event.parentId === null) {
        return;
      }

      // This is a reply - update the parent thread's latestReplies
      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.findAll({
        queryKey: [QueryKey.NodeCommentList, nodeId, shareId],
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              comments: page.comments.map((thread) => {
                if (thread.commentId !== event.parentId) {
                  return thread;
                }

                if (thread.tombstone?.isDeleted) {
                  return thread;
                }

                // Add the new reply to latestReplies
                const newReply: CommentData = {
                  id: event.comment.id,
                  body: event.comment.body,
                  createdBy: event.comment.createdBy,
                  createdAt: event.comment.createdAt,
                  updatedAt: event.comment.updatedAt,
                  parentId: event.comment.parentId,
                };

                const hasReplyAlready = thread.latestReplies.some((reply) => reply.id === newReply.id);

                if (hasReplyAlready) {
                  return thread;
                }

                const matchingOptimisticReplyIndex = thread.latestReplies.findIndex((reply) =>
                  isMatchingOptimisticReply({
                    optimisticReply: reply,
                    nextReply: newReply,
                  }),
                );

                if (matchingOptimisticReplyIndex !== -1) {
                  const nextLatestReplies = [...thread.latestReplies];
                  nextLatestReplies.splice(matchingOptimisticReplyIndex, 1, newReply);

                  return {
                    ...thread,
                    latestReplies: mergeRepliesById(nextLatestReplies),
                  };
                }

                return {
                  ...thread,
                  latestReplies: mergeRepliesById(thread.latestReplies, [newReply]),
                  repliesCount: thread.repliesCount + 1,
                };
              }),
            })),
          };
        });
      });

      // Scroll down if the user is near the bottom of the scroll container
      const el = scrollContainerRef?.current;
      if (el) {
        const { scrollTop, scrollHeight, clientHeight } = el;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
        if (isNearBottom) {
          requestAnimationFrame(() => {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
          });
        }
      }
    },
    [nodeId, queryClient, scrollContainerRef, sortOrder, sortValue, shareId],
  );

  const handleCommentDeleted = useCallback(
    (event: CommentDeletedEvent) => {
      if (event.nodeId !== nodeId) {
        return;
      }

      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.findAll({
        queryKey: [QueryKey.NodeCommentList, nodeId, shareId],
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return applyDeletedCommentTombstoneToCache({
            oldData,
            commentId: event.commentId,
            parentCommentId: event.parentId,
          });
        });
      });
    },
    [nodeId, queryClient, shareId],
  );

  // Subscribe to comment created events

  useEcho(channelName, '.comment.created', handleCommentCreated, [channelName, handleCommentCreated]);

  useEcho(channelName, '.comment.resolutionChanged', handleCommentResolutionChanged, [
    channelName,
    handleCommentResolutionChanged,
  ]);

  useEcho(channelName, '.comment.updated', handleCommentUpdated, [channelName, handleCommentUpdated]);

  useEcho(channelName, '.comment.created', handleReplyCreated, [channelName, handleReplyCreated]);

  useEcho(channelName, '.comment.deleted', handleCommentDeleted, [channelName, handleCommentDeleted]);

  return { channelName };
};
